"use client";

import { useEffect } from "react";
import AppImage from "@/components/ui/AppImage";

export default function AwardsSection() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll")?.forEach((el) => {
      observer?.observe(el);
    });

    return () => observer?.disconnect();
  }, []);

  const stats = [
    { id: "stat_1", value: "50+", label: "Productions Completed" },
    { id: "stat_2", value: "25", label: "Industry Awards" },
    { id: "stat_3", value: "15", label: "Years of Excellence" },
  ];

  const awardLogos = [
    { id: "award_1", src: "https://via.placeholder.com/150x80/FFD700/000000?text=Best+Film", alt: "Best Film Award logo" },
    { id: "award_2", src: "https://via.placeholder.com/150x80/E50914/FFFFFF?text=Excellence", alt: "Excellence in Production Award logo" },
    { id: "award_3", src: "https://via.placeholder.com/150x80/9333EA/FFFFFF?text=Creative", alt: "Creative Achievement Award logo" },
    { id: "award_4", src: "https://via.placeholder.com/150x80/10B981/FFFFFF?text=Innovation", alt: "Innovation Award logo" },
    { id: "award_5", src: "https://via.placeholder.com/150x80/F59E0B/000000?text=Director", alt: "Best Director Award logo" },
    { id: "award_6", src: "https://via.placeholder.com/150x80/FFD700/000000?text=Cinema", alt: "Cinema Excellence Award logo" },
  ];

  return (
    <section id="awards" className="bg-[#080808] text-foreground py-24 md:py-32 relative border-t border-white/5">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <span className="text-xs uppercase tracking-[0.3em] text-secondary font-medium mb-4 block">
            Recognition
          </span>
          <h2 className="text-3xl md:text-5xl font-display font-light text-white mb-4">
            Award-Winning Excellence
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {stats?.map((stat, index) => (
            <div
              key={stat?.id}
              className={`text-center p-8 glass-card rounded-3xl animate-on-scroll [animation:animationIn_0.8s_ease-out_${
                0.3 + index * 0.1
              }s_both]`}
            >
              <div className="text-5xl md:text-6xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary mb-3">
                {stat?.value}
              </div>
              <p className="text-muted-foreground text-sm uppercase tracking-wider">
                {stat?.label}
              </p>
            </div>
          ))}
        </div>

        {/* Awards Ticker */}
        <div className="relative overflow-hidden py-8 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.6s_both]">
          <div className="flex gap-12 animate-ticker">
            {/* First set */}
            {awardLogos?.map((logo) => (
              <div
                key={logo?.id}
                className="flex-shrink-0 w-32 h-20 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
              >
                <AppImage
                  src={logo?.src}
                  alt={logo?.alt}
                  className="w-full h-full object-contain"
                />
              </div>
            ))}
            {/* Duplicate set for seamless loop */}
            {awardLogos?.map((logo) => (
              <div
                key={`${logo?.id}_duplicate`}
                className="flex-shrink-0 w-32 h-20 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
              >
                <AppImage
                  src={logo?.src}
                  alt={logo?.alt}
                  className="w-full h-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}